import { useState } from "react";
import { downloadQuizCertificateApi } from "../../services/axios/AuthApis/AiLMSApis";
import { Trophy, RotateCcw, Lock, CheckCircle2, XCircle, ChevronRight, Award } from "lucide-react";

const THEME = "#26256b";
const THEME_LIGHT = "#3230a0";
const PASS_MARK = 70;

const QuizComponent = ({
  questions,
  passingScore,
  onComplete,
  isUnlocked,
  employeeId,
  lmsId,
  userName,
  moduleTitle,
}) => {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [saving, setSaving] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [started, setStarted] = useState(false);

  const increaseAttemptCount = async () => {
    try {
      await fetch("/api/lms/increase-quiz-attempt/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee_id: employeeId, lms_id: lmsId }),
      });
    } catch (error) {
      console.error("Error increasing attempt count", error);
    }
  };

  const handleNext = async () => {
    if (selected === null) return;
    try {
      setSaving(true);
      await fetch("/api/lms/save-quiz-answer/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employee_id: employeeId,
          lms_id: lmsId,
          quiz_id: questions[index].id,
          answer: selected,
        }),
      });

      const nextAnswers = [...answers, selected];

      if (index === questions.length - 1) {
        await fetch("/api/lms/submit-quiz/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ employee_id: employeeId, lms_id: lmsId }),
        });
        const correctCount = nextAnswers.filter(
          (a, i) => questions[i].options[a] === questions[i].correctAnswer
        ).length;
        setScore(Math.round((correctCount / questions.length) * 100));
        setCompleted(true);
      } else {
        setAnswers(nextAnswers);
        setIndex(index + 1);
        setSelected(null);
      }
    } catch (error) {
      console.error("Failed to save answer", error);
    } finally {
      setSaving(false);
    }
  };

  // ── Locked ────────────────────────────────────────────────────────────────
  if (!isUnlocked) {
    return (
      <div style={{ fontFamily: "'Inter', sans-serif", maxWidth: 480, margin: "48px auto", padding: "0 16px" }}>
        <div style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 24px rgba(38,37,107,0.08)",
          padding: "48px 32px",
          textAlign: "center",
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%",
            background: `${THEME}12`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 20px",
          }}>
            <Lock size={32} color={THEME} />
          </div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: THEME, margin: "0 0 8px" }}>
            Quiz Locked
          </h3>
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
            Complete the video module to unlock this assessment.
          </p>
        </div>
      </div>
    );
  }

  // ── Instructions ─────────────────────────────────────────────────────────
  if (!started) {
    return (
      <div style={{ fontFamily: "'Inter', sans-serif", maxWidth: 640, margin: "48px auto", padding: "0 16px" }}>
        <div style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 24px rgba(38,37,107,0.08)",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            background: THEME,
            padding: "28px 32px",
            position: "relative",
            overflow: "hidden",
          }}>
            <div style={{
              position: "absolute", right: -40, top: -40,
              width: 180, height: 180, borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
            }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: "rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Trophy size={22} color="#fff" />
              </div>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0 }}>
                  Assessment Instructions
                </h2>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", margin: "2px 0 0" }}>
                  Read carefully before starting
                </p>
              </div>
            </div>
          </div>

          <div style={{ padding: "28px 32px" }}>
            {/* Stats */}
            <div style={{
              display: "flex",
              borderRadius: 12,
              border: `1px solid ${THEME}20`,
              background: `${THEME}06`,
              overflow: "hidden",
              marginBottom: 28,
            }}>
              {[
                { label: "Total Questions", value: questions.length },
                { label: "Passing Score", value: `${PASS_MARK}%` },
                { label: "Max Attempts", value: "3" },
              ].map((stat, i, arr) => (
                <div key={i} style={{
                  flex: 1,
                  padding: "18px 16px",
                  textAlign: "center",
                  borderRight: i < arr.length - 1 ? `1px solid ${THEME}20` : "none",
                }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: THEME }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Rules */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Rules & Guidelines
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "Each question must be submitted before proceeding to the next.",
                  "Once an answer is submitted, it cannot be modified.",
                  "Navigation to previous questions is not permitted.",
                  "Your final score will be displayed after completing all questions.",
                ].map((rule, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: "50%",
                      background: THEME,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, marginTop: 1,
                    }}>
                      {i + 1}
                    </div>
                    <p style={{ fontSize: 14, color: "#4b5563", margin: 0, lineHeight: 1.6 }}>{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={async () => { await increaseAttemptCount(); setStarted(true); }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "11px 28px",
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: `0 4px 14px ${THEME}44`,
                  transition: "opacity 0.15s",
                }}
                onMouseOver={e => e.currentTarget.style.opacity = "0.88"}
                onMouseOut={e => e.currentTarget.style.opacity = "1"}
              >
                Begin Assessment
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Result ────────────────────────────────────────────────────────────────
  if (completed) {
    const passed = score >= PASS_MARK;
    const circumference = 2 * Math.PI * 52;
    const dashOffset = circumference - (score / 100) * circumference;

    return (
      <div style={{ fontFamily: "'Inter', sans-serif", maxWidth: 520, margin: "48px auto", padding: "0 16px" }}>
        <div style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 24px rgba(38,37,107,0.08)",
          overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            background: passed ? THEME : "#dc2626",
            padding: "24px 32px",
            textAlign: "center",
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              {passed ? <Award size={26} color="#fff" /> : <XCircle size={26} color="#fff" />}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: "0 0 4px" }}>
              {passed ? "Assessment Passed" : "Assessment Failed"}
            </h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", margin: 0 }}>
              {passed ? "Congratulations! You've met the passing criteria." : `Minimum required score is ${PASS_MARK}%.`}
            </p>
          </div>

          <div style={{ padding: "32px", textAlign: "center" }}>
            {/* Score ring */}
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 28 }}>
              <svg width={120} height={120} style={{ transform: "rotate(-90deg)" }}>
                <circle cx={60} cy={60} r={52} fill="none" stroke="#f3f4f6" strokeWidth={10} />
                <circle
                  cx={60} cy={60} r={52} fill="none"
                  stroke={passed ? THEME : "#dc2626"}
                  strokeWidth={10}
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 1s ease" }}
                />
              </svg>
              <div style={{ position: "absolute", textAlign: "center" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: passed ? THEME : "#dc2626" }}>{score}%</div>
                <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Score</div>
              </div>
            </div>

            {/* Stats row */}
            <div style={{
              display: "flex", justifyContent: "center", gap: 32,
              padding: "16px 0",
              borderTop: "1px solid #f3f4f6",
              borderBottom: "1px solid #f3f4f6",
              marginBottom: 28,
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
                  {Math.round((score / 100) * questions.length)}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>Correct</div>
              </div>
              <div style={{ width: 1, background: "#f3f4f6" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{questions.length}</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</div>
              </div>
              <div style={{ width: 1, background: "#f3f4f6" }} />
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{PASS_MARK}%</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>Required</div>
              </div>
            </div>

            {passed ? (
                      <button
                onClick={async () => {
                  setDownloadLoading(true);
                  try {
                    const response = await downloadQuizCertificateApi({
                      employeeId,
                      lmsId,
                      score,
                      userName,
                      moduleTitle,
                    });
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Certificate_${lmsId}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Certificate download failed', error);
                  } finally {
                    setDownloadLoading(false);
                  }
                }}
                disabled={downloadLoading}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "11px 28px",
                  background: downloadLoading ? "#94a3b8" : THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: downloadLoading ? "not-allowed" : "pointer",
                  boxShadow: `0 4px 14px ${THEME}44`,
                }}
                onMouseOver={e => { if (!downloadLoading) e.currentTarget.style.opacity = "0.88" }}
                onMouseOut={e => { if (!downloadLoading) e.currentTarget.style.opacity = "1" }}
              >
                <Award size={16} />
                {downloadLoading ? 'Downloading...' : 'Download Certificate'}
              </button>
            ) : (
              <button
                onClick={async () => {
                  await increaseAttemptCount();
                  setIndex(0); setSelected(null); setAnswers([]);
                  setCompleted(false); setScore(0); setStarted(true);
                }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "11px 28px",
                  background: "#fff",
                  color: THEME,
                  border: `2px solid ${THEME}`,
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <RotateCcw size={15} />
                Retry Assessment
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Active Question ───────────────────────────────────────────────────────
  const q = questions[index];
  const progress = ((index + 1) / questions.length) * 100;

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", maxWidth: 680, margin: "48px auto", padding: "0 16px" }}>
      <div style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e5e7eb",
        boxShadow: "0 4px 24px rgba(38,37,107,0.08)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ background: THEME, padding: "20px 28px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>
              Question {index + 1} of {questions.length}
            </div>
            <div style={{
              fontSize: 12, fontWeight: 600,
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              padding: "3px 10px",
              borderRadius: 20,
            }}>
              {Math.round(progress)}% Complete
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ height: 6, background: "rgba(255,255,255,0.2)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${progress}%`,
              background: "#fff",
              borderRadius: 999,
              transition: "width 0.4s ease",
            }} />
          </div>
        </div>

        <div style={{ padding: "28px 28px 24px" }}>
          {/* Question */}
          <h3 style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#111827",
            lineHeight: 1.6,
            marginBottom: 20,
          }}>
            {q.question}
          </h3>

          {/* Options */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {q.options.map((opt, i) => {
              const isSelected = selected === i;
              const label = String.fromCharCode(65 + i);
              return (
                <button
                  key={i}
                  onClick={() => setSelected(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "13px 16px",
                    border: isSelected ? `2px solid ${THEME}` : "2px solid #e5e7eb",
                    borderRadius: 10,
                    background: isSelected ? `${THEME}08` : "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s",
                    width: "100%",
                  }}
                  onMouseOver={e => { if (!isSelected) e.currentTarget.style.borderColor = `${THEME}50`; }}
                  onMouseOut={e => { if (!isSelected) e.currentTarget.style.borderColor = "#e5e7eb"; }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: isSelected ? THEME : "#f3f4f6",
                    color: isSelected ? "#fff" : "#6b7280",
                    fontSize: 13,
                    fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.15s",
                  }}>
                    {label}
                  </div>
                  <span style={{
                    fontSize: 14,
                    color: isSelected ? THEME : "#374151",
                    fontWeight: isSelected ? 600 : 400,
                    lineHeight: 1.5,
                    transition: "color 0.15s",
                  }}>
                    {opt}
                  </span>
                  {isSelected && (
                    <CheckCircle2 size={18} color={THEME} style={{ marginLeft: "auto", flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            marginTop: 24,
            paddingTop: 18,
            borderTop: "1px solid #f3f4f6",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <p style={{ fontSize: 12, color: "#f59e0b", fontWeight: 500, display: "flex", alignItems: "center", gap: 4, margin: 0 }}>
              ⚠ {index === questions.length - 1
                ? "Once you submit, you cannot change your answers."
                : "Once you click Next, you cannot change your answer."}
            </p>
            <button
              onClick={handleNext}
              disabled={selected === null || saving}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 24px",
                background: selected === null || saving ? "#e5e7eb" : THEME,
                color: selected === null || saving ? "#9ca3af" : "#fff",
                border: "none",
                borderRadius: 9,
                fontSize: 14,
                fontWeight: 600,
                cursor: selected === null || saving ? "not-allowed" : "pointer",
                boxShadow: selected === null || saving ? "none" : `0 4px 12px ${THEME}44`,
                transition: "all 0.15s",
              }}
            >
              {saving ? "Saving..." : index === questions.length - 1 ? "Submit" : "Next"}
              {!saving && <ChevronRight size={15} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizComponent;
